import classNames from 'classnames';
import Button from 'components/Button';
import Ticket from './components/Ticket';
import SignedDocument from './components/SignedDocument';
import useRouteHistory from './useRouteHistory';
import stat_time from './icons/stat_time.svg';

import styles from './RouteHistory.module.scss';
import React, {useEffect, useRef, useState} from 'react';
import {get} from 'lodash';
import {add, parseISO} from 'date-fns';
import {utcToZonedTime, format} from 'date-fns-tz';
import FormGroup from 'components/FormGroup';
import PrintIcon from 'icons/PrintIcon';
import ReactToPrint from 'react-to-print';
import Modal from 'components/Modal';
import {Address, Order} from 'types';
import {Link, useLocation} from 'react-router-dom';
import {useForm, useFormState} from 'react-hook-form';
import AssignedHeader from './components/AssignedHeader';
import NewMap from 'pages/DeliveryPlanner/components/NewMap';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import Spinner from 'components/Spinner';
import InboxModal from './components/InboxModal';
import Typography from 'components/Typography';
import NewAddressModal from './components/NewAddressModal';
import ReverseIcon from './icons/reverse';
import ConfirmModal from 'components/ConfirmModal';
import SendBulkMessageModal from 'components/SendBulkMessageModal';

type noteTypes = {
  [key: string]: object;
};

type LocationProps = {
  showPdf: boolean;
  order_id: string;
};

function RouteHistory() {
  const {
    route,
    onChangeCheckbox,
    onChangeAddress,
    sendDriverNote,
    roundedTime,
    currentVehicle,
    vehicles,
    handleDeleteOrder,
    handleDeleteAll,
    handleDragEnd,
    addNewDeliveries,
    deliveries,
    inboxOrders,
    changingPositions,
    isFetching,
    isAddingNewDeliveries,
    activeOrder,
    setActiveOrder,
    customerAddressSelectOptions,
    createNewAddress,
    newAddressModal,
    setNewAddressModal,
    defaultCheckedAddressId,
    setDefaultCheckedAddressId,
    reverseSequence,
    customerIds,
    showSendBulkMessageModal,
    setShowSendBulkMessageModal,
    setCustomerIds,
  } = useRouteHistory();

  const {handleSubmit, control, register, setValue, reset} = useForm();
  const {isSubmitting} = useFormState({control});

  const [orderList, setOrderList] = useState<
    | (Order & {
        preference_date?: string;
        preference_time?: string;
        items?: any[];
      })[]
    | null
  >(null);
  const [noteModal, setNoteModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteAll, setIsDeleteAll] = useState(false);
  const [notes, setNotes] = useState<noteTypes>({});
  const [inboxModal, setInboxModal] = useState(false);
  const [showReverseConfirmModal, setShowReverseConfirmModal] = useState(false);
  const [isDragDropEnabled, setIsDragDropEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('dragDropEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const location = useLocation<LocationProps | null>();

  const documentRef = useRef<HTMLDivElement>(null);
  const foundOrder = useRef(false);

  useEffect(() => {
    if (route && location.state?.showPdf) {
      route.deliveries.forEach((item) => {
        const foundItem = item.orders.find(
          (item) => item.new_leaf_order === String(location.state?.order_id)
        );
        if (foundItem) {
          setOrderList(item.orders);
          setActiveOrder(item);
          setValue('driverNote', get(item, 'note', ''));
          foundOrder.current = true;
        }
      });
    }
  }, [location, notes, route, setActiveOrder, setValue]);

  const handleOrdersClick =
    (order: Order) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setActiveOrder(order);
      setOrderList(order.orders);
      setDefaultCheckedAddressId((order?.delivery_address as Address)?.id);
    };
  const handleNoteClick = (id: number, staff: string) => {
    setNoteModal(true);
    setValue('driverNote', staff ? staff : get(notes[`${id}`], 'note', ''));
  };

  const handleOrdersClose = () => {
    setOrderList(null);
    setNoteModal(false);
    foundOrder.current = false;
  };

  const handleAddNewDeliveries = async (
    deliveryIds: number[],
    callback: () => void
  ) => {
    if (!deliveryIds.length) return;
    try {
      await addNewDeliveries(deliveryIds);
      setInboxModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      // clear checkbox state anytime
      callback();
    }
  };
  const deleteOrder = async () => {
    setDeleting(true);
    if (isDeleteAll) {
      await handleDeleteAll();
    } else {
      await handleDeleteOrder(get(activeOrder, 'id', 0));
    }
    setDeleting(false);
    setDeleteModal(false);
  };

  const noteSubmit = async (value: any) => {
    const body = {
      id: Number(get(activeOrder, 'id', '')),
      note: String(value?.driverNote),
    };
    const res = await sendDriverNote(body);
    if (res.status === 201) {
      setNotes((pr) => ({...pr, [`${get(activeOrder, 'id', '')}`]: body}));
      reset();
      setNoteModal(false);
    }
  };
  const orderListRef = useRef<HTMLDivElement>(null);

  const [globalId, setGlobalId] = useState(0);
  const [openedPopup, setOpenedPopup] = useState({
    index: 0,
    id: 0,
  });
  const isSkippedActiveOrder = (activeOrder?.status as any) === 'cancelled';
  const markerClick = (id: number) => {
    setGlobalId(id);
  };
  const togglePopup = (index: number, id: number) => {
    setOpenedPopup({
      index,
      id,
    });
  };
  const handleOpenMenu = () => {
    setNewAddressModal(true);
  };
  const handleChangeAddress = (selectedAddressId: number) =>
    onChangeAddress(activeOrder?.id!, selectedAddressId, () => {
      setNewAddressModal(false);
      setOrderList(null);
    });

  const toggleDragDrop = () => {
    setIsDragDropEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem('dragDropEnabled', JSON.stringify(newValue));
      return newValue;
    });
  };
  const handleSelectAll = () => {
    if (customerIds.length > 0) {
      setCustomerIds([]);
    } else {
      const newCustomerIds = Array.from(
        new Set(deliveries.map((delivery) => delivery?.customer?.id!))
      );
      setCustomerIds(newCustomerIds);
    }
  };
  return (
    <>
      <div
        ref={orderListRef}
        style={{position: 'absolute', zIndex: -100, display: 'none'}}
        className={classNames(styles.panel__content, styles['--assigned'])}
      >
        {route.deliveries.map((ticket, index) => {
          return (
            <Ticket
              isAssigned={true}
              key={ticket.id}
              isPrint={true}
              showTime={
                route.status === 'processed' || ticket.status === 'completed'
              }
              index={index + 1}
              onChangeCheckbox={onChangeCheckbox}
              onOrdersClick={handleOrdersClick(ticket)}
              {...(ticket as any)}
              handleNoteClick={() => {
                setActiveOrder(ticket);
                handleNoteClick(
                  ticket.id,
                  String(get(ticket, 'staff_note', '') || '')
                );
              }}
            />
          );
        })}
      </div>

      <div className={styles.root}>
        <div className={styles.panel}>
          <AssignedHeader
            driver={
              get(route, 'driver.first_name', '') +
              ' ' +
              get(route, 'driver.last_name', '')
            }
            roundedTime={roundedTime}
            currentVehicle={currentVehicle}
            vehicles={vehicles?.results}
            startLocation={
              route.start_location
                ? {
                    name: route.start_location,
                    lat: route.end_latitude,
                    lng: route.start_longitude,
                  }
                : {
                    name: '1100 2nd Ave, New York, NY 10022',
                    lat: 0,
                    lng: 0,
                  }
            }
            routes={route.deliveries.length}
            start_time={route.start_time}
            endLocation={
              route.end_location
                ? {
                    name: route.end_location,
                    lat: route.end_latitude,
                    lng: route.end_longitude,
                  }
                : {
                    name: '1100 2nd Ave, New York, NY 10022',
                    lat: 0,
                    lng: 0,
                  }
            }
          />
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="routes">
              {(droppableProvided) => {
                return (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    className={classNames(
                      styles.panel__content,
                      styles['--assigned']
                    )}
                  >
                    {deliveries.length === 0 && isFetching && (
                      <Spinner className={styles.fetchLoader} />
                    )}
                    {deliveries.map((ticket, index) => {
                      return (
                        <Draggable
                          isDragDisabled={
                            route.status !== 'awaiting' || !isDragDropEnabled
                          }
                          key={ticket.id}
                          draggableId={String(ticket.id)}
                          index={index}
                        >
                          {(draggableProvided) => {
                            return (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                {...draggableProvided.dragHandleProps}
                              >
                                <Ticket
                                  isAssigned={
                                    route.status !== 'completed' ||
                                    ticket.status === 'completed'
                                  }
                                  togglePopup={togglePopup}
                                  showTime={true}
                                  index={index + 1}
                                  globalId={globalId}
                                  onChangeCheckbox={onChangeCheckbox}
                                  onOrdersClick={handleOrdersClick(ticket)}
                                  selected={customerIds.includes(
                                    ticket?.customer?.id ?? 0
                                  )}
                                  {...(ticket as any)}
                                  flag_reason={
                                    ticket?.orders?.[0]?.flag_reason || ''
                                  }
                                  isCanceled={true}
                                  onDelete={() => {
                                    setIsDeleteAll(false);
                                    setDeleteModal(true);
                                    setActiveOrder(ticket);
                                  }}
                                  handleNoteClick={() => {
                                    setActiveOrder(ticket);
                                    handleNoteClick(
                                      ticket.id,
                                      String(
                                        get(ticket, 'staff_note', '') || ''
                                      )
                                    );
                                  }}
                                />
                              </div>
                            );
                          }}
                        </Draggable>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>

          <div className={styles.panel__assigned_buttom}>
            {route.status === 'awaiting' && (
              <>
                <div className={styles.dragToggleWrapper}>
                  <label className={styles.dragToggle}>
                    <input
                      type="checkbox"
                      checked={isDragDropEnabled}
                      onChange={toggleDragDrop}
                    />
                    <span>Enable drag & drop</span>
                  </label>

                  {customerIds.length > 0 && (
                    <Button
                      color="blue"
                      size="sm"
                      className={styles.sendSMSButton}
                      onClick={() => setShowSendBulkMessageModal(true)}
                    >
                      SMS
                    </Button>
                  )}

                  <Button
                    className={styles.checkboxToggle}
                    onClick={handleSelectAll}
                    color="blue"
                    size="sm"
                  >
                    {customerIds.length > 0 ? 'Deselect' : 'Select All'}
                  </Button>
                </div>
                <div className={styles.row}>
                  <Button
                    onClick={() => {
                      setIsDeleteAll(true);
                      setDeleteModal(true);
                    }}
                    className={styles.cancel}
                    color="danger"
                    size="md"
                    loading={deleting}
                  >
                    Skip remaining
                  </Button>
                  <Button
                    onClick={() => setInboxModal(true)}
                    className={styles.cancel}
                    color="blue"
                    size="md"
                  >
                    Add
                  </Button>
                  <Button
                    onClick={() => setShowReverseConfirmModal(true)}
                    className={styles.reverseButton}
                    color="blue"
                    size="md"
                  >
                    <ReverseIcon width={16} height={16} />
                  </Button>
                </div>
              </>
            )}
            <div className={styles.panel__assigned_summary}>
              <ReactToPrint
                trigger={() => (
                  <button className={styles.panel__deliveries_print_action}>
                    <PrintIcon size={16} />
                  </button>
                )}
                onBeforeGetContent={() => {
                  orderListRef.current?.setAttribute(
                    'style',
                    'display:block; z-index:-1000;position:absolute'
                  );
                }}
                onAfterPrint={() => {
                  orderListRef.current?.setAttribute(
                    'style',
                    'display:none; z-index:-1000;position:absolute'
                  );
                }}
                onPrintError={() => {
                  orderListRef.current?.setAttribute(
                    'style',
                    'display:none; z-index:-1000;position:absolute'
                  );
                }}
                content={() => orderListRef.current}
              />
              <div className={styles.panel__deliveries_end_time}>
                <img alt="timer" src={stat_time} />
                <span>Ends at</span>{' '}
                {route?.start_time && route.total_time
                  ? format(
                      utcToZonedTime(
                        add(parseISO(route.start_time), {
                          minutes: route.total_time,
                        }),
                        'America/New_York'
                      ),
                      'h:mm aaa',
                      {
                        timeZone: 'America/New_York',
                      }
                    )
                  : '-:-- --'}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.mapView}>
          <div className={classNames(styles.panel__header)}>
            <span className={styles.panel__inbox_title}>Preview</span>
          </div>

          <NewMap
            status={route?.status}
            driver={
              route.status !== 'completed' && route.status !== 'cancelled'
                ? {
                    latitude: get(route, 'driver.location.latitude', 0),
                    longitude: get(route, 'driver.location.longitude', 0),
                  }
                : undefined
            }
            driver_id={
              route.status !== 'completed' && route.status !== 'cancelled'
                ? route.driver?.id
                : undefined
            }
            markerClick={markerClick}
            routeType={get(route, 'vehicle.vehicle_type', 'bike')}
            end_location={{
              name: route.end_location,
              lat: route.end_latitude,
              lng: route.end_longitude,
            }}
            start_location={{
              name: route.start_location,
              lat: route.start_latitude,
              lng: route.start_longitude,
            }}
            openedPopup={openedPopup}
            isHistory={true}
            orders={deliveries}
            display={true}
          />
        </div>
        {/* Inbox Deliveries Modal */}
        <InboxModal
          inboxOrders={inboxOrders}
          isShowModal={inboxModal}
          onClose={() => setInboxModal(false)}
          handleOrdersClick={handleOrdersClick}
          onAddNewDeliveries={handleAddNewDeliveries}
          isAdding={isAddingNewDeliveries}
        />

        <Modal
          key={'id'}
          isOpen={!!orderList || noteModal}
          onClose={handleOrdersClose}
          onRequestClose={handleOrdersClose}
          onAfterOpen={() => {
            if (foundOrder.current) {
              documentRef.current?.scrollIntoView();
            }
          }}
          className={styles.list_wrapper}
        >
          <div className={styles.list_innerContainer}>
            {orderList && (
              <>
                <div className={styles.list}>
                  <div
                    className={classNames(
                      styles.list__item_header,
                      styles.list__item
                    )}
                  >
                    <p>Address</p>
                    <div
                      className={classNames(
                        'Select',
                        styles.addressSelect,
                        isSkippedActiveOrder && styles.disabledSelect
                      )}
                      onClick={
                        !isSkippedActiveOrder ? handleOpenMenu : undefined
                      }
                    >
                      <Typography variant="p" style={{marginBottom: 0}}>
                        {get(
                          deliveries?.find(
                            (order) => order?.id === activeOrder?.id
                          )?.delivery_address,
                          'display_address',
                          ''
                        )}
                      </Typography>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      styles.list__item_header,
                      styles.list__item
                    )}
                  >
                    <p>Order #</p>
                    <p>Preference date</p>
                    {/* <p>Count of items</p> */}
                  </div>
                  {orderList?.map(
                    ({
                      new_leaf_order,
                      preference_date,
                      items,
                      preference_time,
                    }) => (
                      <Link
                        key={new_leaf_order}
                        to={`/requests/${new_leaf_order}`}
                      >
                        <div className={styles.list__item}>
                          <p className={styles.list__id}>#M{new_leaf_order}</p>
                          <p className={styles.list__id}>
                            {preference_date || '--/--/----'}
                            {preference_time ? `/${preference_time}` : ''}
                          </p>
                          {/* <p className={styles.list__count}>
                        {items.length}
                      </p> */}
                        </div>
                      </Link>
                    )
                  )}
                </div>
                <div className={styles.list_total}>
                  Total items:
                  {orderList?.reduce(
                    //@ts-ignore
                    (prev, item) => prev + item.items.length,
                    0
                  )}
                </div>
              </>
            )}
            {noteModal && (
              <form
                className={styles.noteFormWrapper}
                onSubmit={handleSubmit(noteSubmit)}
              >
                <FormGroup>
                  <div>Dispatch note for driver:</div>
                  <textarea
                    className={styles.noteInput}
                    rows={5}
                    {...register('driverNote')}
                  />
                </FormGroup>
                <div className={styles.actions}>
                  <Button
                    type="submit"
                    color="green"
                    size="md"
                    loading={isSubmitting}
                  >
                    Save
                  </Button>
                </div>
              </form>
            )}

            <div>Driver note for Dispatch:</div>
            {get(activeOrder, 'note', 'Empty!') !== null ? (
              <textarea
                disabled={true}
                value={get(activeOrder, 'note', 'Empty!') as string}
                className={styles.orderNote}
              />
            ) : (
              <textarea
                disabled={true}
                value={'Empty!'}
                className={styles.orderNote}
              />
            )}

            <SignedDocument
              ref={documentRef}
              driver={route.driver}
              order={activeOrder}
            />
            {!!get(activeOrder, 'delivery_image', null) &&
              get(activeOrder, 'status', '') !== 'completed' && (
                <div className={styles.delivery_image}>
                  <p>Delivery image:</p>
                  <img
                    alt="delivery_image"
                    src={
                      get(activeOrder, 'delivery_image.medium', '') as string
                    }
                  />
                </div>
              )}
          </div>
        </Modal>
        {/* New Address Modal */}
        <NewAddressModal
          defaultCheckedAddressId={defaultCheckedAddressId}
          onClose={() => setNewAddressModal(false)}
          isShowModal={newAddressModal}
          onChangeAddress={handleChangeAddress}
          addressIsUpdating={false}
          options={customerAddressSelectOptions as any}
          onCreateNewAddress={createNewAddress}
        />
        <Modal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          onRequestClose={() => setDeleteModal(false)}
          className={styles.delete_wrapper}
        >
          <p className={styles.modalHeader}>
            {isDeleteAll
              ? 'Are you sure, you want to skip ?'
              : 'Are you sure you want to skip order ?'}
          </p>
          {isDeleteAll ? (
            route.deliveries
              .filter((item) => item.status !== 'completed')
              .map((item) => (
                <div
                  className={classNames(
                    styles.list__item_header,
                    styles.list__item
                  )}
                >
                  <p>Group #</p>
                  <p>{get(item, 'id', 0)}</p>
                </div>
              ))
          ) : (
            <>
              <div
                className={classNames(
                  styles.list__item_header,
                  styles.list__item
                )}
              >
                <p>Group #</p>
                <p>{get(activeOrder, 'id', 0)}</p>
              </div>
              <div
                className={classNames(
                  styles.list__item_header,
                  styles.list__item
                )}
              >
                <p>Delivery address #</p>
                <p>
                  {get(activeOrder, 'delivery_address.display_address', '')}
                </p>
              </div>
            </>
          )}
          <Button
            className={styles.delete}
            onClick={deleteOrder}
            color="danger"
            size="md"
            loading={deleting}
          >
            Skip {isDeleteAll ? 'all' : ''}
          </Button>
        </Modal>
        <ConfirmModal
          openModal={showReverseConfirmModal}
          setOpenModal={setShowReverseConfirmModal}
          onConfirm={() =>
            reverseSequence(() => setShowReverseConfirmModal(false))
          }
          text="Are you sure you want to reverse the sequence of deliveries?"
        />
        <Modal showCloseButton={false} isOpen={changingPositions}>
          <div style={{alignItems: 'center', textAlign: 'center'}}>
            <Spinner className={styles.fetchLoader} />
            <h3>Recalculating arrival times. Please wait...</h3>
          </div>
        </Modal>
        {showSendBulkMessageModal && (
          <SendBulkMessageModal
            isOpen={showSendBulkMessageModal}
            onClose={() => {
              setCustomerIds([]);
              setShowSendBulkMessageModal(false);
            }}
            customerIds={customerIds.filter((id) => id !== 0)}
            title={`Send message to ${
              customerIds.filter((id) => id !== 0).length
            } customers`}
          />
        )}
      </div>
    </>
  );
}

export default RouteHistory;
